import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import InputField from '../../components/ui/InputField';
import SelectField from '../../components/ui/SelectField';
import CustomButton from '../../components/CustomButton';
import {
    useCreateTenantUserMutation,
    useUpdateTenantUserMutation,
} from '../../api/account/accountApiSlice';
import { AccountUser, UserRole } from '../../api/account/accountApiTypes';
import {
    enqueueErrorSnackbar,
    enqueueSuccessSnackbar,
} from '../../action/snackbarCommands';
import { errorMessageFrom } from '../../utils/errorMessage';

interface UserFormDialogProps {
    onClose: () => void;
    /** When set, the dialog edits this user instead of creating a new one. */
    user?: AccountUser;
}

// Roles the owner may assign to a colleague.
const ASSIGNABLE_ROLES: UserRole[] = ['owner', 'manager', 'service'];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;


/**
 * Owner-only dialog to add or edit a colleague on the tenant. Create posts to
 * `POST /admin/account/users`; edit patches `PATCH /admin/account/users/:id`
 * (email is the Auth0 identity, so it's read-only when editing). Both backends
 * are Auth0-backed and not implemented yet — the frontend ships against the
 * documented contracts (#1593); submitting surfaces the backend's response.
 */
const UserFormDialog = ({ onClose, user }: UserFormDialogProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isEdit = !!user;
    const [createTenantUser, { isLoading: isCreating }] = useCreateTenantUserMutation();
    const [updateTenantUser, { isLoading: isUpdating }] = useUpdateTenantUserMutation();
    const isLoading = isCreating || isUpdating;

    const [firstName, setFirstName] = useState(user?.firstName ?? '');
    const [lastName, setLastName] = useState(user?.lastName ?? '');
    const [email, setEmail] = useState(user?.email ?? '');
    const [phone, setPhone] = useState(user?.phone ?? '');
    const [role, setRole] = useState<UserRole>(user?.role ?? 'service');
    const [touched, setTouched] = useState(false);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const firstNameError = touched && !firstName.trim();
    const lastNameError = touched && !lastName.trim();
    // Email is immutable when editing (Auth0 identity), so it's not validated.
    const emailError = !isEdit && touched && !EMAIL_RE.test(email.trim());
    const canSubmit = !!firstName.trim() && !!lastName.trim()
        && (isEdit || EMAIL_RE.test(email.trim()));

    const roleLabels: Record<UserRole, string> = {
        owner: t('account.role_owner'),
        manager: t('account.role_manager'),
        service: t('account.role_service'),
    };

    const handleSubmit = async () => {
        setTouched(true);
        if (!canSubmit) return;
        try {
            if (isEdit && user) {
                await updateTenantUser({
                    id: user.id,
                    body: {
                        firstName: firstName.trim(),
                        lastName: lastName.trim(),
                        phone: phone.trim() || undefined,
                        role,
                    },
                }).unwrap();
                dispatch(enqueueSuccessSnackbar({ message: t('account.users_edit_success'), autoHideDuration: 3000 }));
            } else {
                await createTenantUser({
                    firstName: firstName.trim(),
                    lastName: lastName.trim(),
                    email: email.trim(),
                    phone: phone.trim() || undefined,
                    role,
                }).unwrap();
                dispatch(enqueueSuccessSnackbar({ message: t('account.users_add_success'), autoHideDuration: 3000 }));
            }
            onClose();
        } catch (err) {
            dispatch(enqueueErrorSnackbar({
                message: errorMessageFrom(err, t(isEdit ? 'account.users_edit_error' : 'account.users_add_error'), t),
            }));
        }
    };

    // Portalled to <body> with z above the sidebar (z-[1200]) so the overlay
    // covers the whole app — including the sidebar — not just the content area.
    return createPortal(
        <div className={'fixed inset-0 z-[1300] flex items-center justify-center p-4'}>
            <div className={'fixed inset-0 bg-black bg-opacity-50'} onClick={onClose} />

            <div
                className={'relative z-10 bg-text-contrast rounded-lg shadow-xl w-full max-w-md'}
                role={'dialog'}
                aria-modal={'true'}
            >
                <div className={'p-6'}>
                    <h3 className={'text-lg font-semibold text-foreground mb-1'}>
                        {t(isEdit ? 'account.users_edit_title' : 'account.users_add_title')}
                    </h3>
                    <p className={'text-sm text-foreground/60 mb-5'}>
                        {t(isEdit ? 'account.users_edit_description' : 'account.users_add_description')}
                    </p>

                    <div className={'flex flex-col gap-4'}>
                        <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4'}>
                            <InputField
                                label={t('account.first_name') + ' *'}
                                value={firstName}
                                onChange={setFirstName}
                                error={firstNameError}
                                errorText={t('account.field_required')}
                            />
                            <InputField
                                label={t('account.last_name') + ' *'}
                                value={lastName}
                                onChange={setLastName}
                                error={lastNameError}
                                errorText={t('account.field_required')}
                            />
                        </div>
                        <InputField
                            label={isEdit ? t('account.contact_email') : t('account.contact_email') + ' *'}
                            value={email}
                            onChange={setEmail}
                            type={'email'}
                            disabled={isEdit}
                            error={emailError}
                            errorText={t('account.email_invalid')}
                        />
                        <InputField
                            label={t('account.phone')}
                            value={phone}
                            onChange={setPhone}
                            type={'tel'}
                            placeholder={t('account.phone_placeholder')}
                        />
                        <SelectField
                            label={t('account.role')}
                            value={role}
                            onChange={(v) => setRole(v as UserRole)}
                            options={ASSIGNABLE_ROLES}
                            optionLabels={roleLabels}
                            labelAbove
                        />
                    </div>
                </div>

                <div className={'px-6 py-4 flex flex-row-reverse gap-3'}>
                    <CustomButton
                        title={t(isEdit ? 'account.users_edit_confirm' : 'account.users_add_confirm')}
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        loading={isLoading}
                    />
                    <CustomButton
                        title={t('common.cancel')}
                        onClick={onClose}
                        isFilled={false}
                    />
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default UserFormDialog;
